import React from 'react';
import { Helmet } from 'react-helmet';
import { trackEvent } from '../utils/analytics';
import { ANALYTICS_EVENTS } from '../utils/analyticsEvents';
import { APP_STORE_URL, buildAppUrl, GOOGLE_PLAY_URL } from '../utils/appLinks';
import { buildTitle, DEFAULT_OG_IMAGE, SITE_URL } from '../utils/seo';

export default function AppLanding() {
  const appHeroLink = buildAppUrl({
    medium: 'cta',
    location: 'app_landing_hero',
    plan: 'entry',
  });
  const appGhanaLink = buildAppUrl({
    medium: 'path_card',
    location: 'app_landing_path_card',
    journey: 'ghana',
  });
  const appGermanyLink = buildAppUrl({
    medium: 'path_card',
    location: 'app_landing_path_card',
    journey: 'germany',
  });
  const appFreeLink = buildAppUrl({
    medium: 'comparison_free',
    location: 'app_landing_comparison',
    plan: 'free',
  });
  const appProLink = buildAppUrl({
    medium: 'comparison_pro',
    location: 'app_landing_comparison',
    plan: 'pro',
  });

  return (
    <>
      <Helmet>
        <title>{buildTitle('Language App')}</title>
        <meta
          name="description"
          content="SistaKyerewaa language app for Ghana and Germany paths with AI coaching, cultural context, and integration-focused learning."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/app`} />
        <meta property="og:title" content={buildTitle('Language App')} />
        <meta property="og:description" content="Dual-path language learning for Ghana and Germany with practical integration guidance and cultural context." />
        <meta property="og:image" content={DEFAULT_OG_IMAGE} />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={buildTitle('Language App')} />
        <meta property="twitter:description" content="Dual-path language learning for Ghana and Germany with practical integration guidance and cultural context." />
        <meta property="twitter:image" content={DEFAULT_OG_IMAGE} />
        <link rel="canonical" href={`${SITE_URL}/app`} />
      </Helmet>

      <main className="max-w-6xl mx-auto px-6 py-16">
        <section className="border border-stone-200 bg-white/70 p-8 md:p-12">
          <p className="text-xs uppercase tracking-[0.35em] text-[#7b7671]">Language App</p>
          <h1 className="font-display text-4xl md:text-5xl text-[#1c1b1a] mt-4">
            Learn German with diaspora-first guidance
          </h1>
          <p className="text-[#6a6661] mt-5 max-w-3xl">
            SistaKyerewaa App gives you a dual-path journey for Ghana or Germany, AI coaches with cultural context,
            and integration-focused language practice built for real life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mt-8">
            <a
              href={appHeroLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                trackEvent(ANALYTICS_EVENTS.APP_OPEN_CLICK, {
                  location: 'app_landing_hero',
                  plan: 'entry',
                  outbound: true,
                });
              }}
              className="inline-flex items-center justify-center px-8 py-3 bg-[#2d2b29] text-[#f5f4f2] text-sm uppercase tracking-[0.25em] hover:bg-[#1f1d1b] transition-colors"
            >
              Start Learning App
            </a>
            <a
              href="/book"
              className="inline-flex items-center justify-center px-8 py-3 border border-[#c9c5bf] text-sm uppercase tracking-[0.25em] text-[#2d2b29] hover:bg-[#e7e4df] transition-colors"
            >
              Book Human Support
            </a>
          </div>
          <div className="flex flex-wrap gap-3 mt-5">
            <a
              href={GOOGLE_PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Get SistaKyerewaa on Google Play"
              className="inline-flex min-w-[172px] items-center gap-3 rounded-lg bg-[#1c1b1a] px-4 py-2.5 text-white shadow-sm transition-transform hover:-translate-y-0.5"
            >
              <svg viewBox="0 0 512 512" className="h-8 w-8 shrink-0" aria-hidden="true">
                <path fill="#00D2FF" d="M48 32 301 256 48 480Z" />
                <path fill="#00E676" d="m48 32 319 184-66 40Z" />
                <path fill="#FFEA00" d="m48 480 319-184-66-40Z" />
                <path fill="#FF3D71" d="m367 216 89 51c11 6 11 22 0 28l-89 51-66-40Z" />
              </svg>
              <span className="leading-tight">
                <span className="block text-[10px] uppercase tracking-[0.15em]">Get it on</span>
                <span className="block text-base font-medium">Google Play</span>
              </span>
            </a>
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Download SistaKyerewaa on the App Store"
              className="inline-flex min-w-[172px] items-center gap-3 rounded-lg bg-[#1c1b1a] px-4 py-2.5 text-white shadow-sm transition-transform hover:-translate-y-0.5"
            >
              <svg viewBox="0 0 384 512" className="h-8 w-8 shrink-0 fill-current" aria-hidden="true">
                <path d="M318.7 268.7c-.2-36.7 16.4-64.3 49.8-84.7-18.7-26.8-47-41.5-84.2-44.4-35.2-2.8-73.7 20.5-87.8 20.5-14.9 0-49-19.5-75.8-19.5C65.3 141.5 6.3 184.8 6.3 273c0 26.1 4.8 53 14.4 80.7 12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.9-17.9 31.9 0 48.3 17.9 76.5 17.9 48.6-.7 90.4-82.5 102.6-119.3-67.7-31.9-64.2-89-64.2-90.9zM262.6 105.1c27.1-32.2 24.6-61.5 23.8-72.1-23.9 1.4-51.6 16.3-67.4 34.7-17.4 19.7-27.6 44-25.4 71.5 25.8 2 49.6-11.2 69-34.1z" />
              </svg>
              <span className="leading-tight">
                <span className="block text-[10px] tracking-[0.06em]">Download on the</span>
                <span className="block text-base font-medium">App Store</span>
              </span>
            </a>
          </div>
        </section>

        <section className="grid gap-6 md:grid-cols-2 mt-10">
          <a
            href={appGhanaLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackEvent(ANALYTICS_EVENTS.APP_PATH_SELECT, {
                path: 'ghana',
                location: 'app_landing_path_card',
                outbound: true,
              });
            }}
            className="border border-stone-200 bg-white/70 p-6 hover:border-[#bdb7b0] transition-colors"
          >
            <p className="text-xs uppercase tracking-[0.25em] text-[#7b7671]">Ghana Path</p>
            <h2 className="font-display text-3xl text-[#2d2b29] mt-3">Prepare before migration</h2>
            <p className="text-[#6a6661] mt-3">
              Focused on Goethe exam readiness, visa interview confidence, and practical language for embassy processes.
            </p>
          </a>
          <a
            href={appGermanyLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackEvent(ANALYTICS_EVENTS.APP_PATH_SELECT, {
                path: 'germany',
                location: 'app_landing_path_card',
                outbound: true,
              });
            }}
            className="border border-stone-200 bg-white/70 p-6 hover:border-[#bdb7b0] transition-colors"
          >
            <p className="text-xs uppercase tracking-[0.25em] text-[#7b7671]">Germany Path</p>
            <h2 className="font-display text-3xl text-[#2d2b29] mt-3">Integrate with confidence</h2>
            <p className="text-[#6a6661] mt-3">
              Learn language and norms for Anmeldung, workplace communication, and daily bureaucracy with dignity.
            </p>
          </a>
        </section>

        <section className="grid gap-6 md:grid-cols-3 mt-10">
          <div className="border border-stone-200 bg-white/70 p-6">
            <h3 className="font-display text-2xl text-[#2d2b29]">AI Coaches</h3>
            <p className="text-[#6a6661] mt-2">Choose Sista or Bra Kwame and keep the same mentor experience across lessons and practice.</p>
          </div>
          <div className="border border-stone-200 bg-white/70 p-6">
            <h3 className="font-display text-2xl text-[#2d2b29]">Cultural Context</h3>
            <p className="text-[#6a6661] mt-2">Each lesson explains why phrasing works in Germany and how to avoid common diaspora pitfalls.</p>
          </div>
          <div className="border border-stone-200 bg-white/70 p-6">
            <h3 className="font-display text-2xl text-[#2d2b29]">Milestones</h3>
            <p className="text-[#6a6661] mt-2">Progress through real-world wins like appointment confidence, workplace communication, and integration milestones.</p>
          </div>
        </section>

        <section className="border border-stone-200 bg-white/70 p-8 md:p-10 mt-10">
          <p className="text-xs uppercase tracking-[0.25em] text-[#7b7671]">Compare Plans</p>
          <h2 className="font-display text-3xl text-[#2d2b29] mt-3">Free vs Sista Pro</h2>
          <div className="mt-6 grid gap-6 md:grid-cols-2">
            <div className="border border-stone-200 p-6">
              <p className="text-xs uppercase tracking-[0.25em] text-[#7b7671]">Free</p>
              <h3 className="font-display text-2xl text-[#2d2b29] mt-2">Start at no cost</h3>
              <ul className="mt-4 space-y-2 text-[#6a6661]">
                <li>Dual-path intro lessons (Ghana + Germany)</li>
                <li>Core AI coach practice sessions</li>
                <li>Milestone progress tracking</li>
              </ul>
              <a
                href={appFreeLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  trackEvent(ANALYTICS_EVENTS.APP_PLAN_SELECT, {
                    plan: 'free',
                    location: 'app_landing_comparison',
                    outbound: true,
                  });
                }}
                className="inline-flex items-center justify-center mt-6 px-6 py-3 bg-[#2d2b29] text-[#f5f4f2] text-xs uppercase tracking-[0.25em] hover:bg-[#1f1d1b] transition-colors"
              >
                Start Free
              </a>
            </div>
            <div className="border border-stone-200 p-6">
              <p className="text-xs uppercase tracking-[0.25em] text-[#7b7671]">Sista Pro</p>
              <h3 className="font-display text-2xl text-[#2d2b29] mt-2">Deeper integration support</h3>
              <ul className="mt-4 space-y-2 text-[#6a6661]">
                <li>Advanced workplace + integration modules</li>
                <li>Pronunciation support and richer AI feedback</li>
                <li>Priority support and Pro-only sessions (rolling out)</li>
              </ul>
              <a
                href={appProLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  trackEvent(ANALYTICS_EVENTS.APP_PLAN_SELECT, {
                    plan: 'pro',
                    location: 'app_landing_comparison',
                    outbound: true,
                  });
                }}
                className="inline-flex items-center justify-center mt-6 px-6 py-3 border border-[#c9c5bf] text-xs uppercase tracking-[0.25em] text-[#2d2b29] hover:bg-[#e7e4df] transition-colors"
              >
                Explore Pro
              </a>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
